export const GOOGLE_TRANSLATE_LANGUAGES = [
    { code: 'en', label: 'English', native: 'English', country: 'in' },
    { code: 'ta', label: 'Tamil', native: 'தமிழ்', country: 'in' },
    { code: 'hi', label: 'Hindi', native: 'हिन्दी', country: 'in' },
    { code: 'te', label: 'Telugu', native: 'తెలుగు', country: 'in' },
    { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ', country: 'in' },
    { code: 'ml', label: 'Malayalam', native: 'മലയാളം', country: 'in' },
    { code: 'bn', label: 'Bengali', native: 'বাংলা', country: 'in' },
    { code: 'mr', label: 'Marathi', native: 'मराठी', country: 'in' },
    { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી', country: 'in' },
    { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ', country: 'in' },
    { code: 'ur', label: 'Urdu', native: 'اردو', country: 'in' },
];

export const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
const STORAGE_KEY = 'gettimelam_translate_lang';
const CHOSEN_KEY = 'gettimelam_language_chosen';

export const clearGoogTransCookie = () => {
    const expire = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = expire;

    const parts = window.location.hostname.split('.');
    for (let index = 0; index < parts.length - 1; index += 1) {
        const domain = parts.slice(index).join('.');
        document.cookie = `${expire} domain=${domain};`;
        document.cookie = `${expire} domain=.${domain};`;
    }
};

const setGoogTransCookie = code => {
    clearGoogTransCookie();
    if (code === 'en') return;

    document.cookie = `googtrans=/en/${code}; max-age=31536000; path=/; SameSite=Lax`;
};

export const getStoredTranslateLanguage = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return GOOGLE_TRANSLATE_LANGUAGES.some(language => language.code === stored) ? stored : 'en';
};

export const markLanguageChosen = code => {
    const supported = GOOGLE_TRANSLATE_LANGUAGES.some(language => language.code === code);
    localStorage.setItem(STORAGE_KEY, supported ? code : 'en');
    localStorage.setItem(CHOSEN_KEY, 'true');
};

export const hasChosenLanguage = () => {
    const alreadyChosen = localStorage.getItem(CHOSEN_KEY) === 'true'
        || localStorage.getItem(STORAGE_KEY) !== null;
    if (alreadyChosen) localStorage.setItem(CHOSEN_KEY, 'true');
    return alreadyChosen;
};

export const loadGoogleTranslate = () => {
    window.googleTranslateElementInit = () => {
        if (typeof window.google?.translate?.TranslateElement !== 'function') return;

        const element = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);
        if (!element || element.childElementCount) return;

        new window.google.translate.TranslateElement(
            {
                pageLanguage: 'en',
                includedLanguages: GOOGLE_TRANSLATE_LANGUAGES.map(language => language.code).join(','),
                autoDisplay: false,
            },
            GOOGLE_TRANSLATE_ELEMENT_ID
        );
    };

    const existingScript = document.getElementById('google_translate_script');
    if (existingScript) {
        if (window.google?.translate) {
            window.googleTranslateElementInit();
        } else {
            existingScript.addEventListener('load', window.googleTranslateElementInit, { once: true });
        }
        return;
    }

    const script = document.createElement('script');
    script.id = 'google_translate_script';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
};

// Applying the target before a reload is more reliable than driving Google's
// private hidden selector, which can be unavailable on a slow connection.
export const applyGoogleTranslateLanguage = requestedCode => {
    const supported = GOOGLE_TRANSLATE_LANGUAGES.some(language => language.code === requestedCode);
    const code = supported ? requestedCode : 'en';

    markLanguageChosen(code);
    document.documentElement.lang = code;
    setGoogTransCookie(code);
    window.location.reload();
};

// Called before loading Google's script so direct visits and refreshed routes
// consistently use the previously selected language.
export const prepareStoredGoogleTranslateLanguage = () => {
    const code = getStoredTranslateLanguage();
    document.documentElement.lang = code;
    setGoogTransCookie(code);
};

// Google Translate often misses text that React mounts after the page loaded (e.g. footer categories fetched
// from the API). Re-selecting the current language in its hidden dropdown makes it translate the new nodes.
export const retranslatePage = () => {
    const code = getStoredTranslateLanguage();
    if (code === 'en') return;
    const select = document.querySelector('select.goog-te-combo');
    if (!select) return;
    select.value = code;
    select.dispatchEvent(new Event('change'));
};

// Google Translate only translates what is on the page when it runs, and text that React renders later (a dashboard
// tab, a modal, data from the API) stays English. This watches for newly added text and asks Google to translate
// again, at most once every couple of seconds. Google's own changes (<font> wrappers) are ignored so it cannot loop.
export const startAutoRetranslate = () => {
    if (typeof window === 'undefined' || window.__gtAutoRetranslate) return;
    window.__gtAutoRetranslate = true;
    let timer = null;
    let last = 0;
    const GAP = 2500;
    const run = () => { timer = null; last = Date.now(); retranslatePage(); };
    const schedule = () => {
        if (timer) return;
        timer = setTimeout(run, Math.max(600, last + GAP - Date.now()));
    };
    const fromGoogle = node => {
        const el = node.nodeType === 1 ? node : node.parentElement;
        return !el || el.tagName === 'FONT' || Boolean(el.closest && el.closest('.skiptranslate, #google_translate_element, .goog-te-menu-frame'));
    };
    new MutationObserver(mutations => {
        if (getStoredTranslateLanguage() === 'en') return;
        const fresh = mutations.some(m => [...m.addedNodes].some(node => (node.nodeType === 1 || node.nodeType === 3) && !fromGoogle(node)));
        if (fresh) schedule();
    }).observe(document.body, { childList: true, subtree: true });
};
